import React, { useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useFormContext, useWatch } from 'react-hook-form';
import Typography from '@mui/material/Typography';
import Timeline from '#component/shipment/form/delivery/timeline';
import { nextStep, setAfterSubmitAction, setFormStatus } from '#action/shipment/form';
import { submitAndGoBack } from '#component/shipment/form/use-step-submit';
import PreloaderComponent from '#component/preloader';
import propTypes from './prop-types';
import {
  StyledPaper,
  ButtonCnt,
  ErrorCtn,
  StyledButton,
  FormWrapper,
  PreloaderCtn
} from './style';
import { useIntersection } from '#hooks';
import { requestStatuses as statuses } from '#helper';

const intersectionOptions = {
  rootMargin: '-69px',
  threshold: [1]
};

const Form = props => {
  const { sentinelRef, isIntersecting } = useIntersection(intersectionOptions);
  const displayFields = useSelector(s => s.delivery, shallowEqual);
  const methods = useFormContext();
  const { control, handleSubmit, trigger, formState } = methods || {};
  const { isValid, isSubmitted } = formState || {};
  const statesErrors = formState?.errors?.states;
  const error = statesErrors?.root?.message ?? statesErrors?.message;
  const states = useWatch({ control, name: 'states' });
  const originDate = states?.[0]?.date;

  // RHF does not clear the array level error when a nested field changes
  useEffect(() => {
    if (isSubmitted) {
      trigger?.('states');
    }
  }, [originDate, isSubmitted, trigger]);

  const {
    dispatch,
    submitting,
    shipment,
    editable,
    SaveButton = () => null,
    CancelButton = () => null,
    formStatus,
    onFormSubmit
  } = props;
  const { loading, failed } = statuses;
  const formLoading = formStatus === loading;

  const handleBackClick = () => submitAndGoBack(dispatch, methods, onFormSubmit);

  const handleNextClick = () => {
    dispatch(setFormStatus(loading));

    const next = () => {
      dispatch(nextStep());
    };

    dispatch(setAfterSubmitAction(next));

    if (!isValid) {
      dispatch(setFormStatus(failed));
    }
  };

  const isLoading = isValid && (!displayFields?.length || formLoading);

  return (
    <FormWrapper
      autoComplete="off"
      onSubmit={handleSubmit?.(onFormSubmit)}
      isLoading={isLoading}
    >
      {isLoading ? (
        <PreloaderCtn>
          <PreloaderComponent />
        </PreloaderCtn>) : null}
      <StyledPaper
        elevation={0}
        isLoading={isLoading}
      >
        <Timeline
          states={shipment.states}
          disabled={!editable}
        />
      </StyledPaper>
      <div ref={sentinelRef} />
      <ErrorCtn>
        {error && <Typography>{error}</Typography>}
      </ErrorCtn>
      <ButtonCnt hasBorderTop={!isIntersecting} isLoading={isLoading}>
        <StyledButton as={CancelButton} />
        <StyledButton
          disabled={submitting}
          type="button"
          variant="outlined"
          onClick={handleBackClick}
        >
          Back
        </StyledButton>
        <StyledButton
          disabled={submitting}
          type="submit"
          variant="outlined"
          onClick={handleNextClick}
        >
          Next
        </StyledButton>
        {shipment.id && editable && (
        <StyledButton as={SaveButton} disabled={submitting} />
        )}
      </ButtonCnt>
    </FormWrapper>
  );
};

Form.propTypes = propTypes;

export default Form;
